import React, { useState, useEffect } from 'react';
import { X, Trash2, ArrowLeft, Plus, AlertTriangle, Lock } from 'lucide-react';
import {
  SEVERITY, SEVERITY_ORDER, OSHA_CATEGORY, categoriesForSeverity,
  parseDateKey, formatTime, formatDateTime,
} from '../constants';

// Container: shows the day's entry list, or the create/edit form.
export default function DayModal({
  dateKey, dayEntries, currentUserId, canLog, canManage,
  onClose, onCreate, onUpdate, onDelete,
}) {
  const [mode, setMode] = useState(dayEntries.length > 0 ? 'list' : 'new'); // 'list' | 'new' | entryId

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // If the list empties (last entry deleted), close out.
  useEffect(() => {
    if (mode === 'list' && dayEntries.length === 0) onClose();
  }, [mode, dayEntries.length, onClose]);

  const date = parseDateKey(dateKey);
  const dateStr = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const canGoBack = dayEntries.length > 0;

  const editing = mode !== 'list' && mode !== 'new' ? dayEntries.find(e => e.id === mode) : null;
  const isForm = mode === 'new' || !!editing;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-700 px-6 py-4 flex items-start justify-between">
          <div className="flex items-start gap-3">
            {isForm && canGoBack && (
              <button onClick={() => setMode('list')} className="p-1 rounded hover:bg-slate-700 text-slate-300 mt-0.5" title="Back to list">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">
                {dateKey}{isForm ? (editing ? ' · Edit Entry' : ' · New Entry') : ` · ${dayEntries.length} ${dayEntries.length === 1 ? 'Entry' : 'Entries'}`}
              </div>
              <h3 className="text-lg font-semibold text-white mt-0.5">{dateStr}</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-700 text-slate-300"><X className="w-5 h-5" /></button>
        </div>

        {mode === 'list' ? (
          <ListView
            entries={dayEntries}
            canLog={canLog}
            onAdd={() => setMode('new')}
            onSelect={(id) => setMode(id)}
            onClose={onClose}
          />
        ) : (
          <EntryForm
            dateKey={dateKey}
            existing={editing}
            currentUserId={currentUserId}
            canLog={canLog}
            canManage={canManage}
            canGoBack={canGoBack}
            onBack={() => setMode('list')}
            onClose={onClose}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onDelete={onDelete}
            afterSave={() => (canGoBack ? setMode('list') : onClose())}
            afterDelete={() => (dayEntries.length > 1 ? setMode('list') : onClose())}
          />
        )}
      </div>
    </div>
  );
}

function Badge({ entry }) {
  if (entry.category !== OSHA_CATEGORY) return null;
  return (
    <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${entry.oshaNotified ? 'bg-slate-600 text-white' : 'bg-red-600 text-white'}`}>
      OSHA {entry.oshaNotified ? '✓' : '!'}
    </span>
  );
}

function ListView({ entries, canLog, onAdd, onSelect, onClose }) {
  return (
    <>
      <div className="p-6 space-y-3 flex-1">
        {entries.map((entry) => {
          const sev = SEVERITY[entry.severity];
          if (!sev) return null;
          return (
            <button
              key={entry.id}
              onClick={() => onSelect(entry.id)}
              className="w-full text-left rounded-md border border-slate-700 hover:border-blue-500 transition-colors flex overflow-hidden"
            >
              <div className={`w-1.5 ${sev.bar} flex-shrink-0`} />
              <div className="flex-1 p-3 bg-slate-900/40">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[11px] font-bold tracking-wider text-slate-300">{sev.code} · {sev.label.toUpperCase()}</span>
                  <Badge entry={entry} />
                </div>
                {entry.category && <div className="font-semibold text-white">{entry.category}</div>}
                {entry.notes && <div className="text-sm text-slate-300 mt-1 whitespace-pre-wrap line-clamp-2">{entry.notes}</div>}
                <div className="text-[11px] tracking-wider text-slate-500 mt-2">
                  LOGGED BY {(entry.loggedBy || 'UNKNOWN').toUpperCase()}
                  {entry.loggedAt && ` · ${formatTime(entry.loggedAt)}`}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="border-t border-slate-700 px-6 py-4 flex justify-between items-center">
        <button onClick={onClose} className="text-sm px-4 py-2 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-700">Close</button>
        {canLog && (
          <button onClick={onAdd} className="text-sm px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Entry
          </button>
        )}
      </div>
    </>
  );
}

function EntryForm({
  dateKey, existing, currentUserId, canLog, canManage, canGoBack,
  onBack, onClose, onCreate, onUpdate, onDelete, afterSave, afterDelete,
}) {
  const [severity, setSeverity] = useState(existing?.severity || '');
  const [category, setCategory] = useState(existing?.category || '');
  const [notes, setNotes] = useState(existing?.notes || '');
  const [oshaNotified, setOshaNotified] = useState(existing?.oshaNotified || false);
  const [oshaReference, setOshaReference] = useState(existing?.oshaReference || '');
  const [justification, setJustification] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!confirmDelete) return;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const isOwn = existing && existing.loggedById === currentUserId;
  const oshaLocked = existing?.oshaNotified;
  // Who may edit THIS entry.
  const editable = existing
    ? (oshaLocked ? canManage : (canManage || (canLog && isOwn)))
    : canLog;
  const readOnly = !editable;
  const needsJustification = editable && oshaLocked;

  const needsDetails = severity && severity !== 'green';
  const canSave =
    !readOnly && !saving && severity &&
    (severity === 'green' || (category && notes.trim())) &&
    (!needsJustification || justification.trim());

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    const payload = {
      incidentDate: dateKey,
      severity,
      category: severity === 'green' ? null : category,
      notes: severity === 'green' ? null : notes.trim(),
      oshaNotified: severity !== 'green' && category === OSHA_CATEGORY ? oshaNotified : false,
      oshaReference: severity !== 'green' && category === OSHA_CATEGORY ? oshaReference.trim() : '',
    };
    if (needsJustification) payload.justification = justification.trim();
    try {
      if (existing) await onUpdate(existing.id, payload);
      else await onCreate(payload);
      afterSave();
    } catch (e) {
      setError(e?.message || 'Save failed.');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existing) return;
    try {
      await onDelete(existing.id);
      afterDelete();
    } catch (e) {
      setError(e?.message || 'Delete failed.');
      setConfirmDelete(false);
    }
  };

  const inputCls = 'w-full bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-white focus:border-blue-500 outline-none disabled:opacity-60';

  return (
    <>
      <div className="p-6 space-y-6">
        {readOnly && (
          <div className="flex items-center gap-2 text-sm text-slate-300 bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2">
            <Lock className="w-4 h-4 text-slate-400" />
            {oshaLocked && existing
              ? 'This entry is OSHA-notified and can only be edited by a Safety Lead.'
              : 'You can view this entry but do not have permission to edit it.'}
          </div>
        )}

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 mb-3 block">Status *</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {SEVERITY_ORDER.map((key) => {
              const sev = SEVERITY[key];
              const selected = severity === key;
              return (
                <button
                  key={key}
                  disabled={readOnly}
                  onClick={() => {
                    setSeverity(key);
                    if (category && !categoriesForSeverity(key).includes(category)) setCategory('');
                  }}
                  className={`p-3 rounded-md border text-left transition-colors disabled:opacity-60 ${
                    selected ? 'border-blue-500 bg-slate-700' : 'border-slate-600 bg-slate-900 hover:border-slate-400'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-sm ${sev.dot}`} />
                    <span className="text-[11px] font-bold tracking-wider text-slate-300">{sev.code}</span>
                  </div>
                  <div className="font-semibold mt-1 text-white text-sm">{sev.label}</div>
                </button>
              );
            })}
          </div>
        </div>

        {needsDetails && (
          <>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Category *</label>
              <select value={category} disabled={readOnly} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                <option value="">Select category...</option>
                {categoriesForSeverity(severity).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Notes *</label>
              <textarea
                value={notes} disabled={readOnly} rows={4}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Brief description of what occurred..."
                className={`${inputCls} resize-none`}
              />
            </div>

            {category === OSHA_CATEGORY && (
              <div className="rounded-md border border-red-600/60 bg-red-950/30">
                <div className="border-b border-red-600/60 bg-red-600 text-white px-4 py-2 flex items-center gap-2 rounded-t-md">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-xs font-bold tracking-widest">OSHA REPORTING DEADLINES</span>
                </div>
                <div className="p-4 space-y-3 text-sm">
                  <div className="text-slate-200 space-y-1.5">
                    <div><span className="font-bold">Fatality:</span> report within 8 hours of finding out.</div>
                    <div><span className="font-bold">In-patient hospitalization, amputation, or loss of an eye:</span> report within 24 hours.</div>
                  </div>
                  <div className="text-[11px] tracking-wider text-slate-400 border-t border-red-900/60 pt-2">
                    REPORT TO OSHA: 1-800-321-OSHA (6742) · OSHA.GOV/REPORT
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer pt-1">
                    <input type="checkbox" checked={oshaNotified} disabled={readOnly} onChange={(e) => setOshaNotified(e.target.checked)} className="mt-0.5 w-4 h-4 accent-red-600" />
                    <span className="text-slate-100 font-semibold">OSHA has been notified</span>
                  </label>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">OSHA Reference / Case # (optional)</label>
                    <input type="text" value={oshaReference} disabled={readOnly} onChange={(e) => setOshaReference(e.target.value)} placeholder="Confirmation number from OSHA report" className={inputCls} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {needsJustification && (
          <div className="rounded-md border border-amber-500/50 bg-amber-950/20 p-4">
            <label className="text-xs uppercase tracking-widest text-amber-300 mb-2 block">Edit Justification *</label>
            <p className="text-xs text-slate-400 mb-2">This entry was filed with OSHA. Editing requires a justification note, which is recorded in the audit trail.</p>
            <textarea value={justification} rows={2} onChange={(e) => setJustification(e.target.value)} placeholder="Reason for changing an officially filed record..." className={`${inputCls} resize-none`} />
          </div>
        )}

        {existing && (
          <div className="text-[11px] tracking-wider text-slate-500 border-t border-slate-700 pt-3 space-y-1">
            <div>CREATED: {formatDateTime(existing.loggedAt)} BY {(existing.loggedBy || 'UNKNOWN').toUpperCase()}</div>
            {existing.lastEditedAt && <div>LAST EDITED: {formatDateTime(existing.lastEditedAt)} BY {(existing.lastEditedBy || 'UNKNOWN').toUpperCase()}</div>}
          </div>
        )}

        {error && <div className="text-sm text-red-400">{error}</div>}
      </div>

      <div className="border-t border-slate-700 px-6 py-4 flex items-center justify-between">
        <div>
          {existing && canManage && (
            <button
              onClick={() => (confirmDelete ? handleDelete() : setConfirmDelete(true))}
              className={`text-sm px-3 py-2 rounded-md border flex items-center gap-2 transition-colors ${
                confirmDelete ? 'border-red-600 bg-red-600 text-white hover:bg-red-700' : 'border-red-600 text-red-400 hover:bg-red-600 hover:text-white'
              }`}
            >
              <Trash2 className="w-4 h-4" /> {confirmDelete ? 'Confirm Delete' : 'Delete'}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={canGoBack ? onBack : onClose} className="text-sm px-4 py-2 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-700">
            {canGoBack ? 'Back' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={`text-sm px-4 py-2 rounded-md text-white ${canSave ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-600 cursor-not-allowed opacity-60'}`}
            >
              {saving ? 'Saving...' : 'Save Entry'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
